import * as React from "react";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import Link from "@mui/material/Link";
import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import * as Yup from "yup";
import { Formik } from "formik";
import { useNavigate } from "react-router-dom";
import useAuth from "../hooks/useAuth";
import { useEffect } from "react";
import LoadingSpinner from "../components/LoadingSpinner";
import googleImage from "../assets/images/google.png";
import { Avatar, Divider, Stack } from "@mui/material";
import JoinNowImage from "../assets/images/joinnow.png";
import { ParseQueryString } from "../utils/apiService";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
declare const window: any; // Needed for Hubspot

export default function JoinNow() {
    const navigate = useNavigate();
    const { signInGoogle, isAuthenticated, isAuthenticating } = useAuth();

    useEffect(() => {
        // Log Pageview with Hubspot
        const _hsq = window._hsq = window._hsq || [];
        _hsq.push(['setPath', '/signup']);
        _hsq.push(['trackPageView']);

        const queryParams = ParseQueryString(location.search)
        const sf = queryParams.get("sf")

        if (sf && sf != "") {
            navigate(`/sign-up?sf=${sf}`);
        }

        // Store UTM parameters in localStorage so that they can be used later
        // This is useful for tracking the source of the sign-up
        (function () {
            const params = new URLSearchParams(window.location.search);
            const utmKeys = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'];

            utmKeys.forEach(key => {
                const value = params.get(key);
                if (value) {
                    localStorage.setItem(key, value);
                } else {
                    const cookieValue = getCookie(key);
                    if (cookieValue) {
                        localStorage.setItem(key, cookieValue);
                    }
                }
            });
        })();
    }, []);

    const validationSchema = Yup.object({
        email: Yup.string()
            .email("Enter a valid email")
            .required("Email is required"),
    });

    useEffect(() => {
        if (isAuthenticated) {
            navigate("/", { replace: true });
        }
    }, [isAuthenticated]);

    const getLogoImage = () => {
        let baseURL = location.origin

        if (baseURL.indexOf("localhost") >= 0) {
            baseURL = "https://dev.portal.theshare.group"
        }

        return baseURL + "/storefront/tsg_logo_dark.png"
    }

    if (isAuthenticating || isAuthenticated) {
        return <LoadingSpinner title="Signing In..." textColor="#0352fc" />;
    }

    const googleSignIn = async () => {
        await signInGoogle()
    }

    // Helper to read a cookie by name
    function getCookie(name: string) {
        const match = document.cookie.match('(^|;)\\s*' + name + '\\s*=\\s*([^;]+)');
        if (!match) return '';
        return decodeURIComponent(match.pop() || '');
    }


    return (
        <Box sx={{ backgroundColor: "black", height: "100vh" }} justifyContent="center" display="flex" >
            <Box sx={{ position: "absolute", top: 30, left: 50 }}>
                <Link href="https:/theshare.group" style={{ textDecoration: "none" }}>
                    <img src={getLogoImage()} height={48} />
                </Link>
            </Box>

            <Grid container spacing={8} sx={{ mt: -10, mb: 0 }} justifyContent="center" alignItems="center">
                <Grid item maxWidth={600}>

                    <Box
                        sx={{
                            marginTop: 0,
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            borderRadius: 4,
                            backgroundColor: "#18181b",
                            padding: 2,
                            paddingBottom: 4,
                        }}
                    >

                        <Typography variant="h4" sx={{ marginTop: 2, fontWeight: "bold", color: "white" }} align={"center"}>
                            Create your free account to search your market
                        </Typography>

                        <Typography variant="h6" sx={{ color: "#878790", marginTop: 2, fontWeight: "bold" }} align={"center"}>
                            Free plan available. Instant access to view available leads in your market - no payment required
                        </Typography>

                        <Button
                            type="button"
                            fullWidth
                            variant="contained"
                            onClick={googleSignIn}
                            sx={{
                                backgroundColor: "#27272a",
                                color: "white",
                                my: 2,
                                padding: 2,
                                ":hover": {
                                    backgroundColor: "black",
                                },
                                borderRadius: 4,
                                textTransform: "none",
                                fontSize: 16,
                            }}
                            startIcon={
                                <Avatar src={googleImage} sx={{ width: 20, height: 20 }} />
                            }
                        >
                            Continue with Google
                        </Button>

                        <Grid item sx={{ mb: 2, mt: 1, width: "100%" }}>
                            <Divider sx={{
                                "&::before, &::after": {
                                    borderColor: "#606060",
                                },
                            }}>
                                <Typography sx={{ fontSize: 16, color: "#878790" }}>
                                    or continue with email
                                </Typography>
                            </Divider>
                        </Grid>

                        <Grid item sx={{ width: "100%" }}>
                            <Formik
                                initialValues={{
                                    email: "",
                                    password: "",
                                    submit: null,
                                }}
                                validationSchema={validationSchema}
                                onSubmit={async (values) => {
                                    navigate(`/sign-up?email=${values.email}`);
                                }}
                            >
                                {({
                                    errors,
                                    handleChange,
                                    handleSubmit,
                                    touched,
                                    values,
                                }): JSX.Element => (
                                    <Box
                                        component="form"
                                        onSubmit={handleSubmit}
                                        noValidate
                                        sx={{ mt: 1 }}
                                    >
                                        <Grid container spacing={2}>
                                            <Grid item xs={12}>
                                                <TextField
                                                    required
                                                    fullWidth
                                                    id="email"
                                                    label="Email Address"
                                                    name="email"
                                                    value={values?.email}
                                                    onChange={handleChange}
                                                    error={Boolean(touched.email && errors.email)}
                                                    helperText={touched.email && errors.email}
                                                    autoComplete="email"
                                                    sx={{
                                                        borderRadius: 4,
                                                        backgroundColor: "#27272a",
                                                        input: { color: 'white' },
                                                        label: { color: 'white' },
                                                    }}
                                                />
                                            </Grid>

                                            <Grid item xs={12}>
                                                <Button
                                                    type="submit"
                                                    fullWidth
                                                    variant="contained"
                                                    sx={{
                                                        backgroundColor: "#fafafa",
                                                        color: "black",
                                                        mb: 2,
                                                        padding: 2,
                                                        ":hover": {
                                                            backgroundColor: "#d4d3d3",
                                                        },
                                                        borderRadius: 4,
                                                        textTransform: "none",
                                                        fontSize: 16,
                                                    }}
                                                >
                                                    Continue with Email
                                                </Button>
                                            </Grid>
                                        </Grid>

                                    </Box>
                                )}
                            </Formik>
                        </Grid>

                        <Typography sx={{ color: "#878790", marginTop: 2 }} align={"center"}>
                            By continuing, you agree to the <Link href="https://www.theshare.group/terms" target="_blank" underline="hover" color="#0352fc">Terms of Service</Link>.
                        </Typography>

                        <Typography sx={{ color: "#878790" }} align={"center"}>
                            Read our <Link href="https://www.theshare.group/privacy" target="_blank" underline="hover" color="#0352fc">Privacy Policy</Link>.
                        </Typography>
                    </Box>

                </Grid>
                <Grid item sx={{ display: { xs: "none", sm: "block" } }} >
                    <Typography sx={{ color: "#ed3d2d", marginTop: 2, fontWeight: "bold", fontSize: "1.0rem" }} align={"center"}>
                        #1 SOURCE FOR MOTIVATED SELLER LEADS
                    </Typography>

                    <Stack direction="row" spacing={1} alignItems="center" justifyContent="center" sx={{ mt: 1 }}>
                        <Typography sx={{ color: "white", fontWeight: "bold", fontSize: "1.5rem" }} align={"center"}>
                            Trusted by over
                        </Typography>
                        <Typography sx={{ color: "#ed3d2d", fontWeight: "bold", fontSize: "1.5rem" }} align={"center"}>
                            10,000+
                        </Typography>
                    </Stack>

                    <Typography sx={{ color: "white", marginTop: 0, fontWeight: "bold", fontSize: "1.5rem" }} align={"center"}>
                        agents, investors and brokers
                    </Typography>
                    <img src={JoinNowImage} alt="Join Now" style={{ width: 395, height: 318 }} />
                </Grid>
            </Grid>
        </Box>
    );
}

